import React, { FunctionComponent, useCallback } from 'react';
import Flex from 'components/Flex';
import Text from 'components/Text';
import Button from 'components/Button';
import useZendeskWidget from 'hooks/useZendeskWidget';
import useProfile from 'hooks/useProfile';
import useNavigator from 'hooks/useNavigator';
import useUser from 'hooks/useUser';
import styled from 'styled-components';

interface ContactSupportViewProps {
  step: number;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  onClose: () => void;
}

const SupportCard: FunctionComponent<{
  text: string;
  buttonLabel: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ text, buttonLabel, onClick, disabled }) => (
  <Wrap>
    <TextWrapper>
      <Text align="center" className="pl16 pr16">
        {text}
      </Text>
    </TextWrapper>
    <StyledContactButton styleType="blueFill" className="m16" onClick={onClick} disabled={disabled}>
      {buttonLabel}
    </StyledContactButton>
  </Wrap>
);

const View: FunctionComponent<ContactSupportViewProps> = ({
  step,
  goToNextStep,
  goToPrevStep,
  onClose,
}) => {
  const {
    isReady: isSupportWidgetReady,
    prefillWidget,
    showWidget,
    openWidget,
  } = useZendeskWidget({ hideLauncher: true });

  const { hasAccess, isNoProfile } = useProfile();
  const { navigateCallback } = useNavigator();
  const { user, fullName } = useUser(false);
  const isAuthorized = !isNoProfile && !!user;
  const canCreateRequests = isAuthorized && hasAccess('requests', 'create');

  const openRequestsForm = useCallback(() => {
    if (canCreateRequests) {
      onClose();
      navigateCallback('/requests/create')();
    } else {
      goToNextStep();
    }
  }, [canCreateRequests, navigateCallback, goToNextStep, onClose]);

  const openSupportWidget = useCallback(() => {
    if (!isSupportWidgetReady) return;

    if (fullName || user?.email) {
      prefillWidget({
        ...(fullName && { name: { value: fullName, readOnly: false } }),
        ...(user.email && { email: { value: user.email, readOnly: false } }),
      });
    }

    showWidget();
    openWidget();
    onClose();
  }, [isSupportWidgetReady, prefillWidget, openWidget, fullName, user?.email, onClose]);

  const goToLogIn = useCallback(() => {
    onClose();
    navigateCallback('/sign-in')();
  }, [navigateCallback, onClose]);

  const technicalSupportCard = (
    <SupportCard
      text="If you are experiencing technical difficulties with the CondoGenie website or app (e.g., login errors, password resets, or system bugs), our technical team is here to help."
      buttonLabel="Contact Technical Support"
      onClick={openSupportWidget}
      disabled={!isSupportWidgetReady}
    />
  );

  if (step === 2) {
    return (
      <Flex direction="column" alignItems="center">
        {isAuthorized ? (
          <Text align="center" className="pl16 pr16">
            The Requests page is not available in your portal. Please contact your condo board or
            management directly outside of the CondoGenie platform.
          </Text>
        ) : (
          <Container wrap="wrap" justifyContent="center" alignItems="stretch">
            <SupportCard
              text="For building-related issues (e.g., heating, parking, maintenance, or noise complaints), you must securely contact your management team."
              buttonLabel="Log In"
              onClick={goToLogIn}
            />
            {technicalSupportCard}
          </Container>
        )}
        <Button styleType="secondary" className="mt16" onClick={goToPrevStep}>
          Back
        </Button>
      </Flex>
    );
  }

  return (
    <Container wrap="wrap" justifyContent="center" alignItems="stretch">
      <SupportCard
        text="I have questions or concerns about my unit, condominium, documents, maintenance or other condominium related issues."
        buttonLabel="Contact your Condo Board or Management"
        onClick={openRequestsForm}
      />
      {technicalSupportCard}
    </Container>
  );
};

export default View;

const Container = styled(Flex)`
  gap: 16px;
  ${(props) => props.theme.media.md`
    flex-direction: column;
  `}
`;

const Wrap = styled(Flex)`
  flex: 1;
  flex-direction: column;
  align-items: center;
`;

const TextWrapper = styled(Flex)`
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledContactButton = styled(Button)`
  border-radius: 8px;
  max-width: 254px;
`;
